import { useCallback, useEffect, useMemo, useState } from 'react'
import { useParams } from 'react-router'
import { Box, Button, Chip, CircularProgress, Divider, Stack, Typography } from '@mui/material'
import dayjs from 'dayjs'
import { toast } from 'react-toastify'
import { EVENT_TYPE_SCREENING } from 'src/consumer/constants'
import { cancelBookedSession } from 'src/consumer/services/featureServices'
import {
  getDataMapFromCommonResponse,
  isCommonResponseSuccessfulV2,
} from 'src/mentor/helpers/utilityFunctions'
import { getAllRolesInBoolean } from 'src/mentor/layout/utils'
import { getSPDataWithParams, putSPDataWithParams } from 'src/supportPortal/services/services'
import { getMinimalMentorData } from 'src/supportPortal/views/therapists/util'
import { getMinimalClinicLocationData } from 'src/supportPortal/views/therapists/clinics/util'

const SCREENER_BY_SLUG_API = '/api/secure/cc/support/screeners/by-slug'
const SCREENER_SCOPE_CONSULTATIONS_API =
  '/api/secure/cc/support/events/booked-slots/screener-scope/all-with-patient-info'
const BOOK_SCREENING_WITH_SCREENER_API =
  '/api/secure/cc/support/therapist/meeting/screening/book-with-screener'
const REASSIGN_SCREENING_API = '/api/secure/cc/support/therapist/meeting/screening/auto-reassign-eligible'
const CANCEL_SCREENING_API = '/api/secure/cc/support/therapist/meeting/booked-slot/cancel'
const UNASSIGN_SCREENING_API = '/api/secure/cc/support/therapist/meeting/screening/unassign-screener'

// previousSteps[].status values coming from the booked-event model.
const SCREENING_STATUS_META = {
  COMPLETED: { label: 'Screening completed', color: 'success' },
  IN_PROGRESS: { label: 'Screening in progress', color: 'info' },
  STARTED: { label: 'Screening started', color: 'info' },
  NOT_STARTED: { label: 'Screening booked', color: 'warning' },
  HANDLER_NOT_ASSIGNED: { label: 'Screening requested (screener unassigned)', color: 'default' },
}

function getScreeningStep(consultation) {
  const steps = Array.isArray(consultation?.previousSteps) ? consultation.previousSteps : []
  return steps.find((s) => s?.refType === EVENT_TYPE_SCREENING && s?.refId) || null
}

function extractScreeningErrorMessage(response) {
  const payload = response?.data || response?.response?.data
  const message = payload?.message || payload?.error || payload?.errorMessage
  const code = payload?.code
  if (message) return code ? `${message} (code ${code})` : message
  const status = response?.status || response?.response?.status
  if (status === 401) return 'Your session has expired. Please sign in again and retry.'
  if (status === 403) return 'You do not have permission to manage screening here (requires L3 therapy support).'
  if (status && status >= 500) return `The server could not process this (HTTP ${status}). Please retry.`
  if (response?.message) return `Could not reach the server: ${response.message}. Check your connection and retry.`
  return 'Screening action failed for an unknown reason. Please retry.'
}

function isOkResponse(resp) {
  return resp?.status === 200 && isCommonResponseSuccessfulV2(resp)
}

export default function ScreenerConsultationsView() {
  const { mentorSlug, clinicLocationSlug } = useParams()
  const canManageScreening = getAllRolesInBoolean().isL3TherapySupport

  const [screener, setScreener] = useState(null)
  const [clinic, setClinic] = useState(null)
  const [loading, setLoading] = useState(true)
  const [consultations, setConsultations] = useState([])
  const [patientInfoById, setPatientInfoById] = useState({})
  const [canViewConsultations, setCanViewConsultations] = useState(true)
  const [actionLoadingId, setActionLoadingId] = useState(null)

  const timeWindow = useMemo(
    () => ({
      startTime: dayjs().startOf('day').subtract(1, 'day').toISOString(),
      endTime: dayjs().startOf('day').add(7, 'day').toISOString(),
    }),
    [],
  )

  const fetchConsultations = useCallback(
    async (screenerData, clinicData) => {
      if (!screenerData?.id) return
      const resp = await getSPDataWithParams(SCREENER_SCOPE_CONSULTATIONS_API, {
        mentorId: screenerData.id,
        'calendar-scope': 'consultations',
        clinicBrandId: clinicData?.clinicBrandId,
        clinicLocationId: clinicData?.id,
        startTime: timeWindow.startTime,
        endTime: timeWindow.endTime,
      })
      if (!isOkResponse(resp)) {
        toast.error(extractScreeningErrorMessage(resp))
        setConsultations([])
        return
      }
      const dataMap = getDataMapFromCommonResponse(resp) || {}
      const bookedEvents = Array.isArray(dataMap.bookedEvents) ? dataMap.bookedEvents : []
      const patientInfo = Array.isArray(dataMap.patientInfo) ? dataMap.patientInfo : []
      const patientMap = patientInfo.reduce((acc, p) => {
        if (p?.id) acc[p.id] = p
        return acc
      }, {})
      const sorted = [...bookedEvents].sort((a, b) =>
        dayjs(a?.startTime).valueOf() - dayjs(b?.startTime).valueOf(),
      )
      setConsultations(sorted)
      setPatientInfoById(patientMap)
      setCanViewConsultations(dataMap.canViewConsultationCalendar !== false)
    },
    [timeWindow.startTime, timeWindow.endTime],
  )

  const setup = useCallback(async () => {
    setLoading(true)
    try {
      const [screenerData, clinicData] = await Promise.all([
        getMinimalMentorData(mentorSlug, undefined, SCREENER_BY_SLUG_API),
        getMinimalClinicLocationData(clinicLocationSlug),
      ])
      setScreener(screenerData)
      setClinic(clinicData)
      await fetchConsultations(screenerData, clinicData)
    } catch (e) {
      console.error('Error setting up screener consultations view', e)
      toast.error('Could not load consultations for this screener and clinic.')
    } finally {
      setLoading(false)
    }
  }, [mentorSlug, clinicLocationSlug, fetchConsultations])

  useEffect(() => {
    setup()
  }, [setup])

  async function handleBookScreening(consultation) {
    if (!screener?.id) return
    setActionLoadingId(consultation?.id)
    try {
      const resp = await putSPDataWithParams(
        BOOK_SCREENING_WITH_SCREENER_API,
        { ...consultation, apiParam: 'booked-event-info' },
        { screenerId: screener.id },
      )
      if (isOkResponse(resp)) {
        toast.success(`Screening booked with ${screener?.name || 'this screener'}`)
        await fetchConsultations(screener, clinic)
      } else {
        toast.error(extractScreeningErrorMessage(resp))
      }
    } finally {
      setActionLoadingId(null)
    }
  }

  async function handleReassignScreening(consultation) {
    const confirmed = window.confirm(
      'Reassign screening via the eligibility flow? This re-evaluates eligible screeners for this clinic/specialty ' +
        'and may affect related screenings. Continue?',
    )
    if (!confirmed) return
    setActionLoadingId(consultation?.id)
    try {
      const resp = await putSPDataWithParams(
        REASSIGN_SCREENING_API,
        { ...consultation, apiParam: 'booked-event-info' },
        { screeningType: consultation?.subTypeOfAppointment || '' },
      )
      if (isOkResponse(resp)) {
        toast.success('Screening reassignment triggered')
        await fetchConsultations(screener, clinic)
      } else {
        toast.error(extractScreeningErrorMessage(resp))
      }
    } finally {
      setActionLoadingId(null)
    }
  }

  async function handleUnassignScreening(consultation) {
    const confirmed = window.confirm(
      'Unassign the screener from this screening? The screening stays booked but moves to the unassigned pool so it ' +
        'can be reassigned or picked up later. Continue?',
    )
    if (!confirmed) return
    setActionLoadingId(consultation?.id)
    try {
      const resp = await putSPDataWithParams(
        UNASSIGN_SCREENING_API,
        { ...consultation, apiParam: 'booked-event-info' },
        {},
      )
      if (isOkResponse(resp)) {
        toast.success('Screener unassigned from the screening')
        await fetchConsultations(screener, clinic)
      } else {
        toast.error(extractScreeningErrorMessage(resp))
      }
    } finally {
      setActionLoadingId(null)
    }
  }

  async function handleCancelScreening(consultation, screeningStep) {
    const screeningEventId = screeningStep?.refId
    if (!screeningEventId) {
      toast.error('Could not find the screening to cancel for this consultation.')
      return
    }
    const reason = window.prompt(
      'Cancel this screening? Optionally add a reason (shown in the booking log):',
      'Cancelled by support',
    )
    // window.prompt returns null when the user dismisses the dialog → abort.
    if (reason === null) return
    setActionLoadingId(consultation?.id)
    try {
      const resp = await cancelBookedSession(
        CANCEL_SCREENING_API,
        screeningEventId,
        reason?.trim() || 'Cancelled by support',
        'none',
      )
      if (isOkResponse(resp)) {
        toast.success('Screening cancelled')
        await fetchConsultations(screener, clinic)
      } else {
        toast.error(extractScreeningErrorMessage(resp))
      }
    } finally {
      setActionLoadingId(null)
    }
  }

  if (loading) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', py: 6 }}>
        <CircularProgress size={28} />
      </Box>
    )
  }

  return (
    <Box sx={{ width: '100%' }}>
      <Box sx={{ mb: 2 }}>
        <Typography variant="subtitle1" sx={{ fontWeight: 600 }}>
          Consultations at {clinic?.title || clinic?.name || 'this clinic'}
        </Typography>
        <Typography variant="body2" color="text.secondary">
          Book or reassign screening for {screener?.name || 'this screener'}. Showing{' '}
          {dayjs(timeWindow.startTime).format('DD MMM')} – {dayjs(timeWindow.endTime).format('DD MMM')}.
        </Typography>
      </Box>

      {!canViewConsultations && (
        <Typography variant="body2" color="text.secondary" sx={{ py: 3 }}>
          This screener doesn’t have consultation-calendar access for any doctor at this clinic, so no consultations
          are listed. Grant consultation access for the relevant doctors in AUMHUM-Control-Center.
        </Typography>
      )}

      {canViewConsultations && consultations.length === 0 && (
        <Typography variant="body2" color="text.secondary" sx={{ py: 3 }}>
          No consultations found at this clinic in this window.
        </Typography>
      )}

      <Stack divider={<Divider flexItem />} spacing={0}>
        {consultations.map((consultation) => {
          const screeningStep = getScreeningStep(consultation)
          const statusMeta = screeningStep
            ? SCREENING_STATUS_META[screeningStep.status] || { label: 'Screening booked', color: 'warning' }
            : null
          const patient =
            patientInfoById[consultation?.patientId] || { name: consultation?.userName }
          const isActing = actionLoadingId === consultation?.id
          const isScreenerAssigned =
            Boolean(screeningStep?.stepHandlerId || screeningStep?.stepHandlerName) &&
            screeningStep?.status !== 'HANDLER_NOT_ASSIGNED'

          return (
            <Box
              key={consultation?.id}
              sx={{
                display: 'flex',
                flexWrap: 'wrap',
                alignItems: 'center',
                justifyContent: 'space-between',
                gap: 1.5,
                py: 1.5,
              }}
            >
              <Box sx={{ minWidth: 220 }}>
                <Typography variant="body2" sx={{ fontWeight: 600 }}>
                  {patient?.name || 'Unknown patient'}
                </Typography>
                <Typography variant="caption" color="text.secondary">
                  {consultation?.startTime ? dayjs(consultation.startTime).format('DD MMM, h:mm A') : 'No time'} ·{' '}
                  Dr. {consultation?.mentorName || 'Unknown'}
                  {consultation?.appointmentNumber ? ` · #${consultation.appointmentNumber}` : ''}
                </Typography>
              </Box>

              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, flexWrap: 'wrap' }}>
                {screeningStep ? (
                  <>
                    <Chip size="small" color={statusMeta.color} label={statusMeta.label} />
                    <Typography variant="caption" color="text.secondary">
                      {screeningStep.stepHandlerName
                        ? `with ${screeningStep.stepHandlerName}`
                        : 'screener not yet assigned'}
                    </Typography>
                    {canManageScreening && (
                      <>
                        <Button
                          size="small"
                          variant="outlined"
                          disabled={isActing}
                          onClick={() => handleReassignScreening(consultation)}
                        >
                          {isActing ? 'Working…' : 'Reassign'}
                        </Button>
                        {isScreenerAssigned && (
                          <Button
                            size="small"
                            variant="outlined"
                            disabled={isActing}
                            onClick={() => handleUnassignScreening(consultation)}
                          >
                            {isActing ? 'Working…' : 'Unassign'}
                          </Button>
                        )}
                        <Button
                          size="small"
                          variant="outlined"
                          color="error"
                          disabled={isActing}
                          onClick={() => handleCancelScreening(consultation, screeningStep)}
                        >
                          {isActing ? 'Working…' : 'Cancel'}
                        </Button>
                      </>
                    )}
                  </>
                ) : (
                  <>
                    <Chip size="small" variant="outlined" label="No screening booked" />
                    {canManageScreening && (
                      <Button
                        size="small"
                        variant="contained"
                        disabled={isActing}
                        onClick={() => handleBookScreening(consultation)}
                      >
                        {isActing ? 'Booking…' : `Book screening with ${screener?.name || 'this screener'}`}
                      </Button>
                    )}
                  </>
                )}
              </Box>
            </Box>
          )
        })}
      </Stack>

      {!canManageScreening && consultations.length > 0 && (
        <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mt: 2 }}>
          Booking/reassigning screening requires the L3 therapy support role.
        </Typography>
      )}
    </Box>
  )
}
